import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import axios from '../../utils/axios';
import { formatINR, formatIndianDate, formatIndianNumber } from '../../utils/currency';

// Import all components
import MetricsCard from './components/MetricsCard';
import AssetDistributionChart from './components/AssetDistributionChart';
import WarrantyTimelineChart from './components/WarrantyTimelineChart';
import AssetAgeHistogram from './components/AssetAgeHistogram';
import StatusSummaryCards from './components/StatusSummaryCards';
import FilterPanel from './components/FilterPanel';
import ExportPanel from './components/ExportPanel';

const AssetAnalyticsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch all data from API
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. Assets
      let assetsData = [];
      try {
        const res = await axios.get('/assets');
        assetsData = res.data.data || res.data || [];
        setAssets(assetsData);
      } catch (e) {
        console.error('Assets API Error:', e);
      }

      // 2. Categories
      try {
        const res = await axios.get('/asset-categories');
        setCategories(res.data.data || res.data || []);
      } catch (e) {
        console.error('Categories API Error:', e);
      }

      // 3. Users
      try {
        const res = await axios.get('/admin/users');
        setUsers(res.data.data || res.data || []);
      } catch (e) {
        console.error('Users API Error:', e);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Unable to load asset analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Map category IDs to names for robust matching
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach(c => {
      map[c.id] = c.name;
    });
    return map;
  }, [categories]);

  // Compute dynamically filtered assets based on active filters
  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      // 1. Department Filter
      if (filters.department && filters.department !== 'all') {
        const assetDept = (asset.department || '').trim().toLowerCase();
        const fDept = filters.department.trim().toLowerCase();
        if (assetDept !== fDept) return false;
      }

      // 2. Asset Category Filter
      if (filters.assetType && filters.assetType !== 'all') {
        const catName = (categoryMap[asset.asset_category_id] || asset.category?.name || '').trim().toLowerCase();
        const fType = filters.assetType.trim().toLowerCase();
        if (catName !== fType) return false;
      }

      // 3. Status Filter
      if (filters.status && filters.status !== 'all') {
        const assetStatus = (asset.status || '').trim().toLowerCase();
        const fStatus = filters.status.trim().toLowerCase();
        if (assetStatus !== fStatus) return false;
      }

      // 4. Location Filter
      if (filters.location && filters.location !== 'all') {
        const assetLoc = (asset.location || '').trim().toLowerCase();
        const fLoc = filters.location.trim().toLowerCase();
        if (assetLoc !== fLoc) return false;
      }

      // 5. Cost Range Filter (in INR)
      if (filters.costRange && filters.costRange !== 'all') {
        const cost = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
        if (filters.costRange === '0-25000' && (cost < 0 || cost > 25000)) return false;
        if (filters.costRange === '25000-50000' && (cost <= 25000 || cost > 50000)) return false;
        if (filters.costRange === '50000-100000' && (cost <= 50000 || cost > 100000)) return false;
        if (filters.costRange === '100000-250000' && (cost <= 100000 || cost > 250000)) return false;
        if (filters.costRange === '250000+' && cost <= 250000) return false;
      }

      // 6. Purchase Date Period Filter
      if (filters.dateRange && filters.dateRange !== 'all') {
        const dateStr = asset.purchase_date || asset.created_at;
        if (!dateStr) return false;
        const pDate = new Date(dateStr);
        if (isNaN(pDate.getTime())) return false;
        
        const now = new Date();
        if (filters.dateRange === '3months') {
          const limit = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          if (pDate < limit) return false;
        } else if (filters.dateRange === '6months') {
          const limit = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          if (pDate < limit) return false;
        } else if (filters.dateRange === '12months') {
          const limit = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
          if (pDate < limit) return false;
        } else if (filters.dateRange === '24months') {
          const limit = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
          if (pDate < limit) return false;
        } else if (filters.dateRange === 'custom') {
          if (filters.startDate) {
            const start = new Date(filters.startDate);
            if (pDate < start) return false;
          }
          if (filters.endDate) {
            const end = new Date(filters.endDate);
            end.setHours(23, 59, 59, 999);
            if (pDate > end) return false;
          }
        }
      }

      return true;
    });
  }, [assets, filters, categoryMap]);

  // Compute dynamic metrics on the filtered dataset
  const metrics = useMemo(() => {
    const totalCount = filteredAssets.length;
    if (totalCount === 0) {
      return {
        totalValue: 0,
        avgValue: 0,
        activeCount: 0,
        spareCount: 0,
        repairCount: 0,
        retiredCount: 0,
        criticalCount: 0,
        utilization: 0,
        warrantyExpiring: 0,
        replacementDue: 0
      };
    }

    const now = new Date();
    let totalValue = 0;
    let activeCount = 0;
    let spareCount = 0;
    let repairCount = 0;
    let retiredCount = 0;
    let criticalCount = 0;
    let warrantyExpiring = 0;
    let replacementDue = 0;

    filteredAssets.forEach(asset => {
      const price = parseFloat(asset.purchase_price) || parseFloat(asset.price) || 0;
      totalValue += price;

      const st = (asset.status || '').toLowerCase().trim();
      if (st === 'active') activeCount++;
      else if (st === 'spare') spareCount++;
      else if (st === 'repair') repairCount++;
      else if (st === 'retired') retiredCount++;

      if (asset.is_critical || asset.condition === 'poor' || asset.condition === 'critical') {
        criticalCount++;
      }

      // Warranty expiring in next 30 days
      if (asset.warranty_expiration) {
        const expDate = new Date(asset.warranty_expiration);
        const daysDiff = (expDate - now) / (1000 * 60 * 60 * 24);
        if (daysDiff >= 0 && daysDiff <= 30) {
          warrantyExpiring++;
        }
      }

      // Replacement due (older than 4 years or past useful life)
      const pDateStr = asset.purchase_date || asset.created_at;
      if (pDateStr) {
        const pDate = new Date(pDateStr);
        const yearsDiff = (now - pDate) / (1000 * 60 * 60 * 24 * 365.25);
        const usefulLife = parseFloat(asset.useful_life_years) || 4;
        if (yearsDiff >= usefulLife || yearsDiff >= 4) {
          replacementDue++;
        }
      }
    });

    const utilization = (activeCount / totalCount) * 100;
    const avgValue = totalValue / totalCount;

    return {
      totalValue,
      avgValue,
      activeCount,
      spareCount,
      repairCount,
      retiredCount,
      criticalCount,
      utilization,
      warrantyExpiring,
      replacementDue
    };
  }, [filteredAssets]);

  // Dynamic KPI Cards
  const keyMetrics = [
    {
      title: 'Total Portfolio Value',
      value: formatINR(metrics.totalValue, true),
      subtitle: `Full: ${formatINR(metrics.totalValue, false)}`,
      change: `Avg: ${formatINR(metrics.avgValue, true)} / asset`,
      changeType: 'neutral',
      icon: 'IndianRupee',
      color: 'primary'
    },
    {
      title: 'Fleet Utilization',
      value: `${metrics.utilization.toFixed(1)}%`,
      subtitle: `${metrics.activeCount} deployed of ${filteredAssets.length} total`,
      change: `${metrics.activeCount} Active`,
      changeType: metrics.utilization >= 70 ? 'increase' : metrics.utilization >= 40 ? 'warning' : 'decrease',
      icon: 'Activity',
      color: 'success'
    },
    {
      title: 'Warranty Expiring (30d)',
      value: formatIndianNumber(metrics.warrantyExpiring),
      subtitle: 'Requires renewal or extended warranty',
      change: metrics.warrantyExpiring > 0 ? `${metrics.warrantyExpiring} Attention` : 'All Covered',
      changeType: metrics.warrantyExpiring > 0 ? 'warning' : 'increase',
      icon: 'AlertTriangle',
      color: 'warning'
    },
    {
      title: 'Replacement Due (4y+)',
      value: formatIndianNumber(metrics.replacementDue),
      subtitle: 'Aged beyond recommended lifecycle',
      change: `${((metrics.replacementDue / (filteredAssets.length || 1)) * 100).toFixed(0)}% Fleet`,
      changeType: metrics.replacementDue > 0 ? 'decrease' : 'neutral',
      icon: 'RefreshCw',
      color: 'accent'
    }
  ];

  // Unique options for FilterPanel
  const uniqueDepartments = useMemo(() => {
    return [...new Set(assets.map(a => a.department).filter(Boolean))].sort();
  }, [assets]);

  const uniqueCategories = useMemo(() => {
    const names = categories.map(c => c.name);
    assets.forEach(a => {
      if (a.category?.name && !names.includes(a.category.name)) {
        names.push(a.category.name);
      }
    });
    return [...new Set(names)].sort();
  }, [assets, categories]);

  const uniqueStatuses = useMemo(() => {
    const list = [...new Set(assets.map(a => a.status).filter(Boolean))];
    if (!list.includes('active')) list.push('active');
    if (!list.includes('spare')) list.push('spare');
    if (!list.includes('repair')) list.push('repair');
    if (!list.includes('retired')) list.push('retired');
    return list;
  }, [assets]);

  const uniqueLocations = useMemo(() => {
    return [...new Set(assets.map(a => a.location).filter(Boolean))].sort();
  }, [assets]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} mt-16 p-6`}>
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-muted-foreground font-medium">Loading live analytics data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-72'} mt-16 p-6`}>
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="text-error text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Analytics</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={handleRefresh} iconName="RefreshCw" iconPosition="left">
                Retry
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Asset Analytics Dashboard - AssetTracker Pro</title>
        <meta name="description" content="Executive-level asset intelligence interface with Indian currency valuation, operational lifecycle metrics, and real-time analytics." />
      </Helmet>
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`transition-layout ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      } mt-16 p-6`}>
        {/* Page Header */}
        <div className="mb-6">
          <Breadcrumb />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
            <div>
              <div className="flex items-center space-x-3 mb-1">
                <h1 className="text-3xl font-bold text-foreground">
                  Asset Analytics Dashboard
                </h1>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                  INR (₹) Standard
                </span>
              </div>
              <p className="text-muted-foreground text-sm">
                Strategic insights, live valuation & lifecycle intelligence for {filteredAssets.length} assets
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-md border border-border">
                Last updated: <span className="font-medium text-foreground">{formatIndianDate(lastUpdated, true)}</span>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                loading={refreshing}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowExportPanel(true)}
                iconName="Download"
                iconPosition="left"
              >
                Export ({filteredAssets.length})
              </Button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="mb-6">
          <FilterPanel
            onFiltersChange={handleFiltersChange}
            isCollapsed={filtersCollapsed}
            onToggleCollapse={() => setFiltersCollapsed(!filtersCollapsed)}
            departments={uniqueDepartments}
            assetTypes={uniqueCategories}
            statuses={uniqueStatuses}
            locations={uniqueLocations}
          />
        </div>

        {/* Dynamic Key Performance Indicators */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground">Key Financial & Operational KPIs</h2>
            <div className="flex items-center space-x-2 text-xs font-medium text-success">
              <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span>Real-time Live Sync</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {keyMetrics.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric.title}
                value={metric.value}
                subtitle={metric.subtitle}
                change={metric.change}
                changeType={metric.changeType}
                icon={metric.icon}
                color={metric.color}
              />
            ))}
          </div>
        </div>

        {/* Secondary Metric Highlights Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="p-3.5 bg-card border border-border rounded-lg shadow-subtle flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Filtered Assets</p>
              <p className="text-xl font-bold text-foreground mt-0.5">{formatIndianNumber(filteredAssets.length)}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
              <Icon name="Box" size={20} />
            </div>
          </div>

          <div className="p-3.5 bg-card border border-border rounded-lg shadow-subtle flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Available Spares</p>
              <p className="text-xl font-bold text-accent mt-0.5">{formatIndianNumber(metrics.spareCount)}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-accent/10 text-accent">
              <Icon name="Package" size={20} />
            </div>
          </div>

          <div className="p-3.5 bg-card border border-border rounded-lg shadow-subtle flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Under Maintenance</p>
              <p className="text-xl font-bold text-warning mt-0.5">{formatIndianNumber(metrics.repairCount)}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-warning/10 text-warning">
              <Icon name="Wrench" size={20} />
            </div>
          </div>

          <div className="p-3.5 bg-card border border-border rounded-lg shadow-subtle flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground font-medium">Critical Equipment</p>
              <p className="text-xl font-bold text-error mt-0.5">{formatIndianNumber(metrics.criticalCount)}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-error/10 text-error">
              <Icon name="ShieldAlert" size={20} />
            </div>
          </div>
        </div>

        {/* Status Summary Overview */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Asset Status & Book Valuation</h2>
              <p className="text-xs text-muted-foreground">Click any card to quickly filter the dashboard by status</p>
            </div>
            {filters.status && filters.status !== 'all' && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilters(f => ({ ...f, status: 'all' }))}
                iconName="X"
                iconPosition="left"
              >
                Clear Status Filter ({filters.status})
              </Button>
            )}
          </div>
          <StatusSummaryCards 
            assets={filteredAssets} 
            activeFilterStatus={filters.status}
            onSelectStatus={(statusId) => {
              setFilters(prev => ({
                ...prev,
                status: prev.status === statusId ? 'all' : statusId
              }));
            }}
          />
        </div>

        {/* Interactive Charts Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
          {/* Asset Distribution */}
          <AssetDistributionChart assets={filteredAssets} categories={categories} />
          
          {/* Warranty Timeline */}
          <WarrantyTimelineChart assets={filteredAssets} />
        </div>

        {/* Asset Age & Lifecycle Analysis */}
        <div className="mb-8">
          <AssetAgeHistogram assets={filteredAssets} categories={categories} />
        </div>

        {/* Live System Integration Status */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-card-foreground">System Integration & Sync Health</h3>
              <p className="text-xs text-muted-foreground">Live connection with database modules</p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2.5 h-2.5 bg-success rounded-full animate-pulse" />
              <span className="text-xs text-success font-semibold">Active & Synced</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3.5 p-3.5 bg-success/5 rounded-lg border border-success/20">
              <div className="p-2 bg-success/10 rounded-lg text-success">
                <Icon name="Database" size={20} />
              </div>
              <div>
                <div className="font-semibold text-card-foreground text-sm">Asset Repository</div>
                <div className="text-xs text-success font-medium">
                  Connected • {formatIndianNumber(assets.length)} assets total ({formatIndianNumber(filteredAssets.length)} filtered)
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3.5 p-3.5 bg-success/5 rounded-lg border border-success/20">
              <div className="p-2 bg-success/10 rounded-lg text-success">
                <Icon name="Users" size={20} />
              </div>
              <div>
                <div className="font-semibold text-card-foreground text-sm">User Management</div>
                <div className="text-xs text-success font-medium">
                  Connected • {formatIndianNumber(users.length)} users registered
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3.5 p-3.5 bg-primary/5 rounded-lg border border-primary/20">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Icon name="Tag" size={20} />
              </div>
              <div>
                <div className="font-semibold text-card-foreground text-sm">Asset Categories</div>
                <div className="text-xs text-primary font-medium">
                  Active • {formatIndianNumber(categories.length)} categories configured
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Real Export Modal */}
        <ExportPanel
          isOpen={showExportPanel}
          onClose={() => setShowExportPanel(false)}
          assets={filteredAssets}
          metrics={metrics}
          categories={categories}
        />
      </main>
    </div>
  );
};

export default AssetAnalyticsDashboard;
